import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, EyeOutlined, HolderOutlined, ImportOutlined, PlusOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import { PayloadEditor } from '@src/frontend/pages/ActionsPage/components/PayloadEditor';
import { useAuthHeaders } from '@src/frontend/hooks/useAuthHeaders';
import { useMessageHandler } from '@src/frontend/hooks/useMessageHandler';
import { useRestAPI } from '@src/frontend/hooks/useRestAPI';
import { useAgentStore } from '@src/frontend/stores/agentStore';
import { useSessionStore } from '@src/frontend/stores/sessionStore';
import { openTiledWindow } from '@src/frontend/utils/screen-window';
import type { UserScriptModelFix } from '@src/frontend/utils/types';
import type { AgentModel, BrowserSessionModel, ScriptResourceModel, UserScriptModel } from '@src/generated/prisma/models';
import type { BrowserScript, ScriptActionNameMap } from '@src/utils/agent';
import { App, Button, Card, Col, Flex, Form, Input, Modal, Radio, Row, Select, Space, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

interface ControlForm {
  session_ids?: string[];
  resources?: Record<string, string[]>;
  actions: BrowserScript[];
}

const actionNames: ScriptActionNameMap = {
  GOTO: '跳转 URL', INPUT: '输入文本', CLICK: '点击元素', SLEEP: '等待',
  WAIT_ELEMENT: '等待元素出现', RUN_JS: '运行脚本',
};
const actionOptions = Object.entries(actionNames).map(([value, label]) => ({ value, label }));
const CONTROL_DRAFT_KEY = 'my-bandai:browser-control:draft';

const InsertActionButton = styled(Button)`
  opacity: .48;
  flex-shrink: 0;
  transition: opacity .16s ease, border-color .16s ease;
  &:hover { opacity: 1; }
`;

export const ControlPage = () => {
  const [form] = Form.useForm<ControlForm>();
  const [saveForm] = Form.useForm<{ name: string; description?: string }>();
  const [importMode, setImportMode] = useState<'replace' | 'append'>('replace');
  const [saveOpen, setSaveOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number>();
  const [draftLoaded, setDraftLoaded] = useState(false);
  const { message } = App.useApp();
  const { getAuthHeader } = useAuthHeaders();
  const { handleMessage } = useMessageHandler();
  const { listReq: agentsReq } = useRestAPI<AgentModel>('/api/agents');
  const { listReq: sessionsReq } = useRestAPI<BrowserSessionModel>('/api/sessions');
  const { listReq: resourcesReq } = useRestAPI<ScriptResourceModel>('/api/resources');
  const { listReq: scriptsReq, createReq } = useRestAPI<UserScriptModel>('/api/scripts');
  const { listReq: publicScriptsReq } = useRestAPI<UserScriptModel>('/api/scripts?publish=true');
  const onlineAgents = useAgentStore(state => state.online);
  const onlineSessions = useSessionStore(state => state.sessions);
  const sessionIds = Form.useWatch('session_ids', form) ?? [];
  const actions = Form.useWatch('actions', form) ?? [];
  const formValues = Form.useWatch([], form);

  useEffect(() => {
    try {
      const draft = localStorage.getItem(CONTROL_DRAFT_KEY);
      if (draft) form.setFieldsValue(JSON.parse(draft));
    } catch {
      localStorage.removeItem(CONTROL_DRAFT_KEY);
    } finally {
      setDraftLoaded(true);
    }
  }, [form]);

  useEffect(() => {
    if (draftLoaded && formValues) localStorage.setItem(CONTROL_DRAFT_KEY, JSON.stringify(formValues));
  }, [draftLoaded, formValues]);

  const scripts = useMemo(() => {
    const privateScripts = (scriptsReq.data?.data ?? []) as UserScriptModelFix[];
    const ids = new Set(privateScripts.map(item => item.id));
    const publicScripts = ((publicScriptsReq.data?.data ?? []) as UserScriptModelFix[]).filter(item => !ids.has(item.id));
    return [...privateScripts, ...publicScripts];
  }, [scriptsReq.data, publicScriptsReq.data]);

  const agentNames = Object.fromEntries((agentsReq.data?.data ?? []).map(agent => [agent.id, agent.name]));
  const sessionOptions = (sessionsReq.data?.data ?? [])
    .filter(session => session.agent_id && onlineAgents[session.agent_id] && onlineSessions.includes(session.id))
    .map(session => ({ label: `${agentNames[session.agent_id!] ?? '未知节点'} / ${session.name}`, value: session.id }));

  const importScript = (scriptId: string) => {
    const imported = scripts.find(item => item.id === scriptId)?.actions as unknown as BrowserScript[] | undefined;
    if (!imported) return;
    const cloned = structuredClone(imported);
    form.setFieldValue('actions', importMode === 'append' ? [...actions, ...cloned] : cloned);
    message.success(`已${importMode === 'append' ? '追加' : '导入'} ${cloned.length} 个动作`);
  };

  const execute = async (selectedActions?: BrowserScript[]) => {
    const target = await form.validateFields(['session_ids', 'resources']);
    const selected = selectedActions ?? await form.validateFields(['actions']).then(value => value.actions);
    const headers = await getAuthHeader();
    if (!headers) return;
    const targets = (target.session_ids ?? []).map(session_id => {
      const session = sessionsReq.data?.data?.find(item => item.id === session_id);
      if (!session?.agent_id) throw new Error(`浏览器 ${session_id} 未绑定 Agent`);
      return { agent_id: session.agent_id, session_id, resource_ids: target.resources?.[session_id] ?? [] };
    });
    await fetch('/api/run-actions', { method: 'PUT', headers, body: JSON.stringify({ useDefaultPage: true, targets, actions: selected }) })
      .then(response => response.json()).then(handleMessage).catch(handleMessage);
  };

  const executeStep = async (index: number) => {
    await form.validateFields([['actions', index], 'session_ids']);
    await execute([form.getFieldValue('actions')[index]]);
  };

  const viewScreen = (sessionId: string) => {
    const agentId = sessionsReq.data?.data?.find(item => item.id === sessionId)?.agent_id;
    if (!agentId) return;
    const params = new URLSearchParams({ agent_id: agentId, session_id: sessionId });
    openTiledWindow(`/sessions/screen?${params}`);
  };

  const saveAsScript = async () => {
    const meta = await saveForm.validateFields();
    const currentActions = await form.validateFields(['actions']).then(value => value.actions);
    const response = await createReq.runAsync({ ...meta, actions: currentActions } as any);
    if (response?.ok) {
      setSaveOpen(false);
      saveForm.resetFields();
      await scriptsReq.runAsync();
    }
  };

  return <Flex className="page-shell" vertical gap={20}>
    <Flex className="page-header" vertical>
      <Typography.Text className="page-eyebrow">LIVE CONTROL</Typography.Text>
      <Typography.Title level={2} className="page-title">浏览器控制台</Typography.Title>
      <Typography.Text type="secondary">选择一个或多个正在运行的浏览器，为每个浏览器配置资源，临时编排动作并立即执行。</Typography.Text>
    </Flex>

    <Form form={form} layout="vertical" initialValues={{ actions: [] }}>
      <Row gutter={[20, 20]}>
        <Col xs={24} xl={8}>
          <Flex vertical gap={16}>
            <Card className="surface-card" title="1. 选择执行目标">
              <Form.Item name="session_ids" label="运行中的浏览器" rules={[{ required: true, message: '至少选择一个浏览器' }]}> 
                <Select allowClear mode="multiple" placeholder="可跨 Agent 选择多个在线浏览器" options={sessionOptions} />
              </Form.Item>
              <Flex vertical gap={12}>{sessionIds.map(sessionId => {
                const session = sessionsReq.data?.data?.find(item => item.id === sessionId);
                return <Card key={sessionId} size="small" title={`${agentNames[session?.agent_id ?? ''] ?? '未知节点'} / ${session?.name ?? sessionId}`} extra={<Button type="text" icon={<EyeOutlined />} onClick={() => viewScreen(sessionId)}>画面</Button>}>
                  <Form.Item name={['resources', sessionId]} label="此浏览器使用的资源" style={{ marginBottom: 0 }}>
                    <Select allowClear mode="multiple" placeholder="按需选择，可与其他浏览器不同" options={(resourcesReq.data?.data ?? []).map(resource => ({ label: resource.name, value: resource.id }))} />
                  </Form.Item>
                </Card>;
              })}</Flex>
            </Card>
            <Card className="surface-card" title="2. 从脚本导入">
              <Flex vertical gap={12}>
                <Radio.Group value={importMode} onChange={event => setImportMode(event.target.value)} options={[{ label: '替换当前队列', value: 'replace' }, { label: '追加到末尾', value: 'append' }]} />
                <Select showSearch optionFilterProp="label" placeholder="选择已有脚本" suffixIcon={<ImportOutlined />} options={scripts.map(script => ({ label: `${script.public ? '共享' : '私有'} · ${script.name}`, value: script.id }))} onChange={importScript} />
              </Flex>
            </Card>
            <Button size="large" icon={<SaveOutlined />} onClick={() => setSaveOpen(true)}>保存为新脚本</Button>
            <Button size="large" type="primary" icon={<SendOutlined />} disabled={!actions.length} onClick={() => execute()}>执行全部动作</Button>
          </Flex>
        </Col>
        <Col xs={24} xl={16}>
          <Card className="surface-card action-queue-card" title={`3. Action 队列 · ${actions.length} 步`}>
            <Form.List name="actions">{(fields, operator) => <Flex className="action-queue-scroll" vertical gap={10}>
              {fields.length > 0 && <InsertActionButton type="dashed" size="small" icon={<PlusOutlined />} onClick={() => operator.add({}, 0)}>在最前面插入</InsertActionButton>}
              {fields.map(({ key, name }, index) => <Flex key={key} vertical gap={10}>
              <Card
                size="small"
                className={dragIndex === index ? 'action-card dragging' : 'action-card'}
                onDragOver={event => event.preventDefault()}
                onDrop={() => { if (dragIndex !== undefined && dragIndex !== index) operator.move(dragIndex, index); setDragIndex(undefined); }}
                title={<Space><HolderOutlined draggable onDragStart={() => setDragIndex(index)} onDragEnd={() => setDragIndex(undefined)} className="drag-handle" />Step {index + 1}</Space>}
                extra={<Space style={{ padding: 4 }}>
                <Button type="text" icon={<SendOutlined />} onClick={() => executeStep(index)}>执行此步</Button>
                <Button type="text" disabled={index === 0} icon={<ArrowUpOutlined />} aria-label="上移" onClick={() => operator.move(index, index - 1)} />
                <Button type="text" disabled={index === fields.length - 1} icon={<ArrowDownOutlined />} aria-label="下移" onClick={() => operator.move(index, index + 1)} />
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => operator.remove(name)} />
              </Space>}>
                <Form.Item label="动作" name={[name, 'action']} rules={[{ required: true }]}><Select options={actionOptions} /></Form.Item>
                <PayloadEditor step={index} name={[name, 'payload']} action={actions[name]} />
              </Card>
              <InsertActionButton type="dashed" size="small" icon={<PlusOutlined />} onClick={() => operator.add({}, index + 1)}>在此处插入</InsertActionButton>
              </Flex>)}
              {!fields.length && <Flex vertical align="center" gap={8} style={{ padding: 48 }}><Typography.Text type="secondary">还没有 Action，可手动添加或从已有脚本导入。</Typography.Text></Flex>}
            </Flex>}</Form.List>
          </Card>
        </Col>
      </Row>
    </Form>

    <Modal open={saveOpen} onCancel={() => setSaveOpen(false)} onOk={saveAsScript} confirmLoading={createReq.loading} title="保存为新脚本">
      <Form form={saveForm} layout="vertical" style={{ marginTop: 20 }}>
        <Form.Item name="name" label="脚本名称" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="description" label="描述"><Input.TextArea /></Form.Item>
      </Form>
    </Modal>
  </Flex>;
};
